import { PermissionProvider } from 'portal-commons';
import { useEffect } from 'react';
import { Route, Switch, useLocation, Redirect } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';

import PageNotFound from './404';
import CustomLoginCallback from './CustomLoginCallback';
import { Home, PrivateRoute } from './components';
import Permissions from './constants/permissions';
import { useAuthContext } from './contexts';
import { authRoutes } from './features/auth/authRoutes';
import campaignsRoutes from './features/campaigns/routes';
import dashboardRoutes from './features/dashboard/routes';
import eventsRoutes from './features/events/routes';
import integrationRoutes from './features/integrations/routes';
import settingsRoutes from './features/settings/routes';
import usersRoutes from './features/users/routes';
import phonesRoutes from './features/phones/routes';
import { IdleWarningCountdownModal } from '@/shared_elements';

const AppWithRouterAccess = () => {
  const { user, role } = useAuthContext();
  const location = useLocation();

  const computeViewName = (routeMatches) => {
    let viewName = '';
    for (let index = 0; index < routeMatches.length; index++) {
      const routeMatch = routeMatches[index];
      const { path } = routeMatch;

      if (!path) {
        return null;
      }

      if (path.startsWith('/')) {
        viewName = path;
      } else {
        viewName += viewName.endsWith('/') ? path : `/${path}`;
      }
    }

    return viewName || '/';
  };

  useEffect(() => {
    const allRoutes = [
      ...authRoutes,
      ...dashboardRoutes,
      ...campaignsRoutes,
      ...settingsRoutes,
      ...integrationRoutes,
      ...usersRoutes,
      ...eventsRoutes,
      ...phonesRoutes,
    ];
    const routeMatches = allRoutes.filter((route) => {
      const regex = new RegExp(route.path.replace(/:[^\/]+/g, '\\w+'));
      return regex.test(location.pathname);
    });
    const viewName = routeMatches && computeViewName(routeMatches);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  }, [location.pathname]);

  return (
    <PermissionProvider permissions={Permissions} role={role}>
      <IdleWarningCountdownModal />
      <Switch>
        <Route
          key="callback"
          exact
          path="/implicit/callback"
          component={CustomLoginCallback}
        />
        <Route key="home" exact path="/" component={Home} />
        {authRoutes.map(({ path, component, key }) => (
          <Route exact path={path} component={component} key={key} />
        ))}
        {[
          ...dashboardRoutes,
          ...campaignsRoutes,
          ...settingsRoutes,
          ...eventsRoutes,
          ...integrationRoutes,
          ...usersRoutes,
          ...phonesRoutes,
        ].map((config) => (
          <PrivateRoute
            key={config.key}
            exact
            path={config.path}
            title={config.title}
            note={config.note}
            component={config.component}
            roles={config.roles}
          />
        ))}
        <Route path="/404" component={PageNotFound} key="404" />
        <Redirect from="*" to={user ? '/404' : '/login'} />
      </Switch>
    </PermissionProvider>
  );
};
export default AppWithRouterAccess;
